import maplibregl, {
	LngLat,
	type LngLatBounds,
	type LngLatLike,
	type Popup,
	type PopupOptions
} from 'maplibre-gl'
import {
	type AdHocMarker,
	type MapMarker,
	type MarkerType,
	type WSMarkerResponse
} from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import { SvelteMap } from 'svelte/reactivity'
import { webSocket } from '$lib/websocket.svelte'
import type { IMessage } from '@stomp/stompjs'
import { type MapSearchOption } from '$mapapp/MapApp/SlideIn/SearchRoutingHistory/MapSearch/MapSearch.Model'
import type { Lot, Pop, Project, slideInModes } from '$mapapp/data/data.model'
import { fsm, type SetEvents as FSMSetEvents, type States as FSMStates } from './fsm.svelte'
import { mapFormControls } from '$lib/map/fak/utils/fakStores'
import { get } from 'svelte/store'
import { syncFakParams } from './url.svelte'
import { SLIDE_IN_WIDTH } from '$lib/maplibre/config'
import { createContext } from 'svelte'
import { LoadingAnimationState } from './loading-animation.svelte'
import { Matching, type MatchingOptions } from './matching.svelte'
import { History } from './history.svelte'
import { RouteState } from './route.svelte'
import type { MiniLot } from '../types/miniLot'
import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
import {
	loadSearchHistoryFromSession,
	saveSearchHistoryToSession
} from '$mapapp/utils/search_history'

export const MAP_BREADCRUMBS_CONTEXT = 'map_slidein'

export type PopupKind = 'info' | 'context' | 'measure'
export type MarkerKind = 'marker' | 'cluster'

type ActivePopup = {
	popup: Popup
	markerId: string
	popupKind: PopupKind
	markerKind: MarkerKind
}

class AppState {
	private readonly SEARCH_HISTORY_LENGTH = 8

	constructor() {
		this.route.init({
			isRoutePlanerOpened: () => this.isRoutePlanerOpened,
			sendRouting: () => this.fsm.send('set_routing')
		})
	}

	previous_state: FSMStates | null = $state(null)

	/** Finite State Machine */
	fsm = fsm

	is_debug = $state(false) // flag from url for global debug mode
	is_beta = $state(false) // flag from url for global beta mode
	/**
	 * true while the map tutorial overlay is open. consumers (e.g. SlideIn fly
	 * transitions, LayerMatchingRadius latches) read this to skip animations and
	 * other delays that interfere with the tutorial's step-by-step pacing.
	 */
	isTutorialMode = $state(false)

	matching: Matching | null = $state(null)
	isMatching = $derived(this.fsm.current === 'matching')

	history = new History()
	canGoBack = $derived(this.isMatching || this.history.canGoBack)

	goBack() {
		// clear the breadcrumbs, if we are in cluster of project view
		if (!appState.isMatching) {
			breadcrumbs.clear(MAP_BREADCRUMBS_CONTEXT)
		}

		this.history.goBack(this)
	}

	/**
	 * coordinator for entering or restarting matching. picks the right path
	 * to populate appState.selectedMarker (read by the Matching constructor)
	 * and previous_state (read by goBack).
	 *
	 * - fresh from a marker: set_<type> with exit_matching modifier captures
	 *   previous_state and bypasses the matching marker_handler intercept
	 * - already in matching: assign selectedMarker directly; the set_<type>
	 *   round-trip would flicker the wrong type/id pair into the URL
	 * - miniLot: skip the marker dispatch — referenceMarker is populated
	 *   later by subscribeToMiniLotCoordinates inside the constructor
	 */
	startMatching(event: { id: string; type: 'pop' | 'lot' | 'miniLot'; options?: MatchingOptions }) {
		if (event.type !== 'miniLot' && event.id) {
			if (this.fsm.current === 'matching') {
				this.selectMarker(`${event.type}-${event.id}`)
			} else if (this.fsm.current === 'cluster') {
				// from a cluster child's "Match finden": don't round-trip through
				// set_<type>, which would overwrite cluster context (selectedId,
				// selectedChildrenIds) and capture previous_state as the child type.
				// assign selectedMarker directly so Matching's constructor sees the
				// reference marker; keep selectedId on the cluster so goBack restores
				// the cluster view, not the single child.
				this.selectedMarker = this.allMarkers.get(`${event.type}-${event.id}`)
			} else {
				this.fsm.send(`set_${event.type}` as FSMSetEvents, {
					id: `${event.type}-${event.id}`,
					flyTo: false,
					modifier: 'exit_matching',
					replaceLastBreadcrumb: true
				})
			}
		}
		this.fsm.send('set_matching', event)
	}

	/** MAP STATE */
	map: maplibregl.Map | null = $state(null)
	is_style_ready = $state(false)

	/** DATA STATE */
	pops: SvelteMap<string, MapMarker> = new SvelteMap()
	lots: SvelteMap<string, MapMarker> = new SvelteMap()
	requests: SvelteMap<string, MapMarker> = new SvelteMap()
	returnFreights: SvelteMap<string, MapMarker> = new SvelteMap()
	projectLots: SvelteMap<string, MapMarker[]> = new SvelteMap()
	projects: SvelteMap<string, MapMarker> = new SvelteMap()
	allMarkers: SvelteMap<string, MapMarker> = new SvelteMap()
	minilotData: MiniLot | null = $state(null)

	setMinilotData(data: MiniLot) {
		this.minilotData = data
	}

	clearMinilotData() {
		this.minilotData = null
	}

	/** POPUP STATE */
	// gates the info popup only; the context menu is unconditional
	enableInfoPopup = $derived(this.isMatching)
	/** single slot for the currently-shown popup; one slot ⇒ one active popup at a time */
	activePopup: ActivePopup | null = $state(null)

	/**
	 * factory for marker popups
	 *
	 * attaches one 'close' listener that clears the active-popup slot on
	 * user-initiated closes (X, ESC, click-outside, map click). use this
	 * instead of `new maplibregl.Popup()` so the listener is attached
	 * exactly once per popup
	 */
	createPopup(options?: PopupOptions): Popup {
		const popup = new maplibregl.Popup(options)
		/**
		 * close event listener
		 *
		 * programmatic closes (showPopup, closePopup) reassign or clear
		 * activePopup before calling remove(), so the comparison against
		 * `popup` fails for those and the listener returns. user-initiated
		 * closes (X button, ESC, click on the map, click outside) still
		 * have activePopup.popup === popup at this point
		 */
		popup.on('close', () => {
			if (this.activePopup?.popup !== popup) return
			this.activePopup = null
		})
		return popup
	}

	showPopup(
		popup: Popup,
		markerId: string,
		popupKind: PopupKind = 'info',
		markerKind: MarkerKind = 'marker'
	) {
		// reassign activePopup before remove() so the createPopup close listener
		// sees activePopup.popup !== popup and treats the close as programmatic
		const previous = this.activePopup
		this.activePopup = { popup, markerId, popupKind, markerKind }
		previous?.popup.remove()
	}

	closePopup() {
		// clear activePopup before remove() (same reason as showPopup)
		const previous = this.activePopup
		this.activePopup = null
		previous?.popup.remove()
	}

	/** INTERACTION STATE */
	// TODO possibly refactor as selectedMarker, then we can give all information to the slide in, the checks can happen via selectedMarker.id
	selectedId: string | null = $state(null)
	selectedMarker: MapMarker | undefined = $state(undefined)
	selectedChildrenIds: string[] = $state([])
	selectedChildren: MapMarker[] | undefined = $state(undefined)

	/**
	 * all adhoc markers, keyed by owner-scoped ids: the search bar and context
	 * menu use the place/marker id directly, the routeplanner prefixes its stop
	 * markers, transportRoute prefixes its endpoint markers. owners remove only
	 * their own keys, so e.g. a route stop marker can coexist with the search
	 * marker for the same place.
	 */
	adhocMarkers: SvelteMap<string, AdHocMarker> = new SvelteMap()

	currentProject: Project | null = $state(null) // TODO use correct type / create new
	currentLot: Lot | null = $state(null) // TODO use correct type / create new
	currentPop: Pop | null = $state(null)

	searchHistory: MapSearchOption[] = $state(loadSearchHistoryFromSession())
	// All route state (stops, polyline, source) lives in its own class.
	route = new RouteState()

	/** UI STATE */
	// FIXME: elimate ambiguity; handle mobile state plus non-state here
	// HINT: we enforce desktop for now; see MapApp.svelte
	is_desktop: boolean | undefined = $state(true)
	is_mobile = $derived(this.is_desktop === false)
	is_search_focussed = $state(false)

	isRoutePlanerOpened: boolean = $state(false)
	isSlideInOpened: boolean = $derived(this.selectedId !== null)
	isSidebarOpened: boolean = $derived(
		(this.is_desktop && (this.isSlideInOpened || this.isRoutePlanerOpened)) || false
	)
	// HINT: we currently enforce desktop
	isDrawerOpened: boolean = false
	// isDrawerOpened: boolean = $derived(
	// 	(this.is_mobile &&
	// 		(this.isSlideInOpened || this.isRoutePlanerOpened || this.is_search_focussed)) ||
	// 		false
	// )
	loadingAnimation = new LoadingAnimationState()
	isFilterOpened: boolean = $state(false)
	isSortOpened: boolean = $state(false)
	isFakOpened: boolean = $state(false)

	/** Any WebSocket stream currently has an active request. */
	isWsLoading: boolean = $derived(webSocket.busyStreams.size > 0)
	/**
	 * Map is mid pan/zoom (or filter-driven re-render) until the next marker render cycle completes.
	 * Flipped true in LayerClusterMarker on `movestart` / `zoomstart` and on the filter-state `$effect`;
	 * flipped back to false inside `scheduleMarkerUpdate` after `updateMarkers()` runs and in the
	 * empty-geojson branch of `initOrUpdateSource`.
	 */
	isMapMoving: boolean = $state(false)
	/** OR of all signals that should make the spinner visible. Add fields above and OR them in to extend. */
	isLoading: boolean = $derived(this.isWsLoading || this.isMapMoving)

	// map meta information
	// The backend's marker requests below need named lon/lat fields, so we
	// pin center to that exact shape instead of MapLibre's looser LngLatLike
	// type (which also allows arrays and {lng, lat}).
	center: { lon: number; lat: number } = $state({ lon: 9.18, lat: 48.78 }) // TODO can be set by tenant
	zoom: number = $state(8)

	/** GETTERS / SETTERS / METHODS */

	addToSearchHistory(search: MapSearchOption) {
		// if the search is already in the list, remove it and add it to the front
		this.searchHistory = this.searchHistory.filter((s) => s.id !== search.id)
		this.searchHistory = [...this.searchHistory, search]
		if (this.searchHistory.length > this.SEARCH_HISTORY_LENGTH) {
			this.searchHistory.shift()
		}
		saveSearchHistoryToSession(this.searchHistory)
	}

	deleteSearchHistory() {
		this.searchHistory = []
		saveSearchHistoryToSession(this.searchHistory)
	}

	/**
	 * select a Marker and open the slide in
	 * possibly refactor with marker (MapMarker) to directly have the information at hand
	 */
	// imperative approach is better for data control flow wrt Marker Data, Slide In Story & Card
	selectMarker(id: string, flyTo: boolean = false) {
		this.selectedId = id
		if (flyTo) {
			const marker = this.allMarkers.get(id)
			if (!marker) {
				console.error('Marker not found', id)
				// TODO: consider using a decorator and stepping out of this else
				// return
			}

			if (marker) {
				const coordinates: LngLatLike = [marker.lon, marker.lat]
				// this.map?.flyTo({ center: coordinates, zoom: 15 })
				this.handleFlyTo(coordinates, 15, true)
			}
		}
		// const marker = this.allMarkers.get(id)

		if (!this.selectedId || !this.allMarkers.has(this.selectedId)) {
			console.error('Marker not found', this.selectedId)
			this.selectedMarker = undefined
			return
		}

		this.selectedMarker = this.allMarkers.get(this.selectedId)
	}

	selectChildren(children: string[]) {
		// this.selectedChildren = children.map((child) => this.allMarkers.get(child))
		this.selectedChildrenIds = children
		if (children.length > 0) {
			this.selectedChildren = children.map((child) => this.allMarkers.get(child) as MapMarker)
		} else {
			this.selectedChildren = undefined
		}
	}

	openFak({ projectId, lotId }: { projectId: string | null; lotId: string | null }) {
		if (!projectId) {
			console.error('Project ID is required to open FaK')
			// FIXME: enable guuard if logic is defined
			// return
		}

		if (projectId) {
			// why
			get(mapFormControls).projectId = projectId
		}

		if (lotId) {
			get(mapFormControls).lotId = lotId
		}

		// Set level based on what's being opened
		if (lotId) {
			get(mapFormControls).level = 3
		} else if (projectId) {
			get(mapFormControls).level = 2
		} else {
			get(mapFormControls).level = 1
		}
		this.isFakOpened = true

		syncFakParams(projectId, lotId)
	}

	closeFak() {
		get(mapFormControls).projectId = null
		get(mapFormControls).lotId = null
		get(mapFormControls).level = 0
		this.isFakOpened = false

		syncFakParams(null, null)
	}

	/**
	 * unselect the marker
	 */
	deselectMarker() {
		this.closePopup()
		this.selectedId = null
		this.selectedMarker = undefined
		// TODO: TBC
		// this.currentProject = null
		// this.currentLot = null
		// this.currentPop = null
	}

	deselectChildren() {
		this.selectedChildrenIds = []
		this.selectedChildren = undefined
	}

	addAdHocMarker(
		marker: MapSearchOption | MapMarker | { id: string; coordinates: LngLatLike },
		// display role LayerRouting renders the marker with; 'other' is the plain adhoc pin
		options: { role: MarkerType; annotationLabel?: string } = { role: 'other' }
	) {
		// Case MapSearchOption: Create MapMarker just with the needed infomation.
		if (typeof marker === 'object' && 'id' in marker && 'coordinates' in marker) {
			const { lng, lat } = LngLat.convert(marker.coordinates!)
			const mapMarker = {
				markerId: marker.id,
				lon: lng,
				lat: lat,
				type: 'other',
				adhocRole: options.role,
				annotationLabel: options.annotationLabel
			} as AdHocMarker
			this.adhocMarkers.set(mapMarker.markerId, mapMarker)
			return
		}

		// Case MapMarker: Just add the marker.
		const mapMarker = marker as MapMarker
		this.adhocMarkers.set(mapMarker.markerId, {
			...mapMarker,
			adhocRole: options.role,
			annotationLabel: options.annotationLabel ?? mapMarker.annotationLabel
		})
	}

	removeAdHocMarker(marker: MapSearchOption | string) {
		if (typeof marker === 'string') {
			this.adhocMarkers.delete(marker)
			return
		}
		this.adhocMarkers.delete(marker.id)
	}

	clearAdHocMarkers() {
		this.adhocMarkers.clear()
	}

	/**
	 * owner-scoped clear: removes only the markers whose key starts with the given prefix
	 */
	removeAdHocMarkersByPrefix(prefix: string) {
		for (const id of this.adhocMarkers.keys()) {
			if (id.startsWith(prefix)) {
				this.adhocMarkers.delete(id)
			}
		}
	}

	handleFlyTo(coordinates: LngLatLike, zoom: number = 15, offset: boolean = false) {
		if (!coordinates) return

		const offsetX = offset && this.is_desktop && this.isSidebarOpened ? SLIDE_IN_WIDTH / 2 : 0
		const offsetY = offset && this.is_mobile && this.isDrawerOpened ? -200 : 0

		this.map?.flyTo({
			center: coordinates,
			zoom: zoom,
			offset: [offsetX, offsetY]
		})
	}

	/** zoom by delta steps from current zoom; offsets camera around the sidebar/drawer
	 *  so the visible map area (not the obscured edge) stays centered. */
	handleZoom(delta: number, offset: boolean = true) {
		if (!this.map) return

		const offsetX = offset && this.is_desktop && this.isSidebarOpened ? SLIDE_IN_WIDTH / 2 : 0
		const offsetY = offset && this.is_mobile && this.isDrawerOpened ? -200 : 0

		this.map.easeTo({
			zoom: this.map.getZoom() + delta,
			offset: [offsetX, offsetY]
		})
	}

	handleFitBounds(
		bounds: LngLatBounds,
		options: { padding: number; maxZoom?: number; animate?: boolean },
		offset: boolean = false
	) {
		const basePadding = options.padding

		const offsetX = offset && this.is_desktop && this.isSidebarOpened ? SLIDE_IN_WIDTH : 0
		const offsetY = offset && this.is_mobile && this.isDrawerOpened ? 350 : 0

		const dynamicPadding = offset
			? {
					top: basePadding,
					bottom: offsetY + basePadding,
					left: offsetX + basePadding,
					right: basePadding
				}
			: { top: basePadding, bottom: window.innerHeight / 2, left: basePadding, right: basePadding }

		this.map?.fitBounds(bounds, {
			...options,
			padding: dynamicPadding
		})
	}
}

// Don't wrap class instance in $state() - class fields already have $state
// See: https://svelte.dev/docs/svelte/$state#Classes
export const appState = new AppState()

/** request lots and pops from the server, given the current center. */
export function requestGeneralMarkers() {
	webSocket.startStreamRequest('/return/markers', '/requestMarker', { ...appState.center })
}

/** request a single marker by type + bare id — fast path for URL deep-links.
 *  Arrives on the same /return/markers topic as the general stream. */
export function requestSingleMarker(type: slideInModes, referenceId: string) {
	webSocket.startStreamRequest('/return/markers', '/requestSingleMarker', {
		referenceType: type,
		referenceId
	})
}

/** request completed lots from the server, given the current center. */
export function requestCompletedLots() {
	webSocket.startStreamRequest('/return/markers', '/requestCompletedLots', { ...appState.center })
}

/** request discarded lots from the server, given the current center. */
export function requestDiscardedLots() {
	webSocket.startStreamRequest('/return/markers', '/requestDiscardedLots', { ...appState.center })
}

/**
 * Subscribe to marker updates from the server. All the updates on markers are sent to the websocket via this route.
 */
export function subscribeToMarkerUpdates() {
	webSocket.addSubscription('/return/markers', (message: IMessage) => {
		let data: WSMarkerResponse

		try {
			data = JSON.parse(message.body)
			for (const marker of data.markers) {
				switch (marker.type) {
					case 'request':
						appState.requests.set(marker.markerId, marker)
						break
					case 'lot': {
						appState.lots.set(marker.markerId, marker)
						// add lot to its project, such that we know here which lots belong to which project
						if (marker.parentId) {
							const actualLotMarkerForProject = appState.projectLots.get(marker.parentId) || []
							actualLotMarkerForProject.push(marker)
							appState.projectLots.set(marker.parentId, [...actualLotMarkerForProject])
						} else {
							console.error('lot marker missing parentId', marker.markerId)
						}
						break
					}
					case 'project':
						appState.projects.set(marker.markerId, marker)
						break
					case 'pop':
						appState.pops.set(marker.markerId, marker)
						break
					case 'returnFreight':
						appState.returnFreights.set(marker.markerId, marker)
						break
				}
				appState.allMarkers.set(marker.markerId, marker)
			}
			webSocket.onStreamMessage('/return/markers', data.requestId, data.endMessage)
		} catch (e) {
			console.error(e)
			throw new Error('Could not parse message body: ' + message.body, { cause: e })
		}
	})
}

// Typed context for the platform request dialog wrapper
export const [getPlatformRequestContext, setPlatformRequestContext] = createContext<{
	open: (lotId: string, popId: string) => void
}>()
